import { getCookie } from './cookies';
import { GA } from '../../ga/ga';
import { GACategoryMappings, GAActionMappings } from '../../constants/gaMappings';
import { Template, User } from '../../store/interfaces';
import { getCurrentLocale } from './cookies';

/**
 * Get current date
 */
export const getCurrentDate = (): Date => new Date();

/**
 * Get the time difference
 */
export const getTimeDifference = (startTime: Date, endTime: Date = new Date()): string =>
  ((+endTime - +startTime) / 1000).toFixed(2);

/**
 * Get current env
 */
export const getCurrentPathname = (): string => window.location.pathname;

/**
 * Get Company Id
 */
export const getCompanyId = (template: Template): string => {
  let company = 'unavailable';
  if (template) {
    company = `${template.companyId}`;
  }
  return company;
};

/**
 * Get Candidate id
 */
export const getCandidateId = (candidateDetails: User): string => {
  let candidate = 'unavailable';
  if (candidateDetails) {
    candidate = `${candidateDetails.candidateId}`;
  }
  return candidate;
};

/** Get Session ID */
export const getSessionId = (): string | undefined =>
  getCookie('WSessionId') ? getCookie('WSessionId') : 'unavailable';

/**
 * Basic GA Events
 */
export const getBasicGAEventsLabel = (
  template: string,
  { templateData, candidate }: { templateData: Template; candidate: User },
): string => {
  const locale = getCurrentLocale();
  const lang = locale.replace(/_/g, '-');
  return `_Template:${template}_Company:${getCompanyId(templateData)}_CandidateId:${getCandidateId(
    candidate,
  )}_Lang:${lang}`;
};

/**
 * GA Video Label
 */
export const getVideoLabel = (
  url: string,
  currentDuration: string | number,
  totalDuration: number | string,
  contentName: string,
  taskName = '',
): string =>
  `_VideoPlayed:true_VideoUrl:${url}_VideoWatchDuration:${currentDuration}sec_VideoDuration:${totalDuration}sec_ContentName:${contentName}_TaskName:${taskName}`;

/**
 * send page view ga data
 */
export const sendPageViewDetails = (pathname = window.location.pathname, pageName: string): void => {
  const GAInstance = GA.getGAInstance();

  if (GAInstance !== null) {
    const { pageview } = GAInstance;
    pageview(pathname, undefined, pageName);
  }
};

/**
 * Send Events Data
 */
export const sendEventsDetails = (
  category: string,
  action: string,
  startTime: Date,
  genericLabel = '',
  template = 'EXP',
  { templateData, candidate }: { templateData: Template; candidate: User },
): void => {
  const GAInstance = GA.getGAInstance();
  let label = getBasicGAEventsLabel(`${template}-${templateData.id}`, { templateData, candidate });
  label = `${label}_TimeSpent:${getTimeDifference(startTime)}sec${genericLabel}`;
  if (GAInstance !== null) {
    const { event } = GAInstance;
    event({ category, action, label });
  }
};

/**
 * GA Event for video specific to General Content Page
 * Modify it to make generic
 */
export const sendGeneralContentVideoDetails = (
  currentDuration: number | string = 'unavailable',
  totalDuration: number | string = 'unavailable',
  pageStartTime: Date,
  url = '',
  generalContentName = '',
  taskName = '',
  { template, candidate }: { template: Template; candidate: User },
): void => {
  const videoLabel = getVideoLabel(url, currentDuration, totalDuration, `GEN-${generalContentName}`, taskName);
  sendEventsDetails(
    GACategoryMappings.GENCONTENT,
    `${GAActionMappings.GENCONTENT} : Video`,
    pageStartTime,
    videoLabel,
    'EXP',
    { templateData: template, candidate },
  );
};

/**
 * GA Event for video specific to General Content Page
 * Modify it to make generic
 */
export const sendTaskListVideoDetails = (
  currentDuration: number | string = 'unavailable',
  totalDuration: number | string = 'unavailable',
  pageStartTime: Date,
  url = '',
  taskListDetail = '',
  { template, candidate }: { template: Template; candidate: User },
): void => {
  const taskListLabel = getVideoLabel(url, currentDuration, totalDuration, `TSK-${taskListDetail}`);
  sendEventsDetails(
    GACategoryMappings.TASKLIST,
    `${GAActionMappings.TASKLIST} : Video`,
    pageStartTime,
    taskListLabel,
    'EXP',
    { templateData: template, candidate },
  );
};

/**
 * GA Event for General content pages
 */
export const sendGeneralContentEventDetails = (
  action: string,
  pageStartTime: Date,
  generalContentName = '',
  taskName = '',
  { template, candidate }: { template: Template; candidate: User },
): void => {
  const label = `_ContentName:GEN-${generalContentName}_TaskName:${taskName}`;
  sendEventsDetails(GACategoryMappings.GENCONTENT, action, pageStartTime, label, 'EXP', {
    templateData: template,
    candidate,
  });
};

/**
 * GA Event for SHL360 Login
 */
export const sendSHL360LoginDetails = (
  loginType = 'Generic',
  pageStartTime: Date,
  { template, candidate }: { template: Template; candidate: User },
): void => {
  const sId = `_SId:${getSessionId()}`;
  let loginLabel = `${sId}_LoginType:${loginType}`;
  let action = GAActionMappings.SHL360_LOGIN;
  switch (loginType) {
    case 'SSO':
      action = GAActionMappings.SHL360_LOGIN_SSO;
      break;
    case 'LINK':
      action = GAActionMappings.SHL360_LOGIN_UNIQUE_LINK;
      loginLabel = `${sId}_LoginType:Unique link`;
      break;
  }
  sendEventsDetails(GACategoryMappings.SHL360_LOGIN, action, pageStartTime, loginLabel, 'SHL360', {
    templateData: template,
    candidate,
  });
};

/**
 * GA Event for SHL360 Task List/Thank You
 */
export const sendSHL360TaskDetails = (
  category: string,
  action: string,
  taskId: number,
  taskType = 'Self',
  pageStartTime: Date,
  extraLabel = '',
  { template, candidate }: { template: Template; candidate: User },
): void => {
  const sId = `_SId:${getSessionId()}`;
  let taskListLabel = `${sId}_TaskId:${taskId}_TaskType:${taskType}`;
  if (extraLabel !== '') {
    taskListLabel = `${taskListLabel}${extraLabel}`;
  }

  sendEventsDetails(category, action, pageStartTime, taskListLabel, 'SHL360', {
    templateData: template,
    candidate,
  });
};
